import { CSSProperties, FC, useMemo } from 'react'

import { Breakpoints } from '@hhgtech/hhg-components'
import { ArticleSvg } from '@hhgtech/icons/together'
import { Box, Grid, rem, SimpleGrid, Stack } from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { BellAlertSvg, StarSparkleSvg, TrendingSvg } from 'components/icons'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { ArticleApi } from 'interfaces/types'

import { useHomeTrendingLastestTopics } from './HomeTrendingLastestTopics.hooks'
import {
  StyledHomeTrendingHeader,
  StyledHomeTrendingList,
  StyledHomeTrendingSegmentedControl,
} from './HomeTrendingLastestTopics.styled'
import { TrendingArticleCard } from './TrendingArticleCard'

export type HomeTrendingLastestTopicsProps = {
  trendingArticles: ArticleApi[]
  className?: string
  style?: CSSProperties
  newsEventsTitle: string
  homeHasMostReadPosts: boolean
}
export const HomeTrendingLastestTopics: FC<HomeTrendingLastestTopicsProps> = (
  props,
) => {
  const {
    trendingArticles = [],
    className,
    style,
    newsEventsTitle,
    homeHasMostReadPosts,
  } = props
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const {
    loading,
    loadingBookmark,
    activeTab,
    onChangeTab,
    trendingPosts: trendingPostsProp,
    bookmarks,
    onClickBookmark,
  } = useHomeTrendingLastestTopics({ trendingArticles })

  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  const postsObj = useMemo(() => {
    const trendingPosts = trendingPostsProp.slice(0, 8)
    const trendingPostsLength = trendingPosts.length
    const aside =
      (trendingPosts.length == 6
        ? 2
        : trendingPostsLength == 7 || trendingPostsLength == 8
        ? 3
        : 4) + 1
    return {
      trendingPosts,
      mainPost: trendingPosts?.[0],
      asidePosts: trendingPosts.slice(1, aside),
      bottomPosts: trendingPosts.slice(aside, trendingPostsLength),
    }
  }, [trendingPostsProp])

  return (
    <Stack
      spacing={24}
      id="trending_lastest_topics"
      className={className}
      style={style}
    >
      <StyledHomeTrendingHeader className="homeTrendingHeader">
        <StyledHomeTrendingSegmentedControl
          size="lg"
          value={activeTab}
          disabled={loading}
          items={[
            {
              key: 'trending',
              icon: <TrendingSvg />,
              label: f({
                id: 'home.trending.trending',
                defaultMessage: 'Trending',
              }),
              'data-event-category': 'Trend Type',
              'data-event-action': 'Trending',
              'data-event-label': `${currentDomain}${basePath}`,
            },
            ...(homeHasMostReadPosts
              ? [
                  {
                    key: 'most_read',
                    icon: <StarSparkleSvg />,
                    label: f({
                      id: 'home.trending.most_read',
                      defaultMessage: 'Most Read',
                    }),
                    'data-event-category': 'Trend Type',
                    'data-event-action': 'Most Read',
                    'data-event-label': `${currentDomain}${basePath}`,
                  },
                ]
              : []),
            {
              key: 'lastest_topics',
              icon: <BellAlertSvg />,
              label: f({
                id: 'home.trending.latest',
                defaultMessage: 'Latest Topics',
              }),
              'data-event-category': 'Trend Type',
              'data-event-action': 'Latest News',
              'data-event-label': `${currentDomain}${basePath}`,
            },
            ...(!!newsEventsTitle
              ? [
                  {
                    key: 'news_events',
                    icon: <ArticleSvg size="1em" />,
                    label: newsEventsTitle,
                    'data-event-category': 'Trend Type',
                    'data-event-action': 'News or Events',
                    'data-event-label': `${currentDomain}${basePath}`,
                  },
                ]
              : []),
          ]}
          onChange={onChangeTab}
          className="homeTrendingSegmentedControl"
        />
      </StyledHomeTrendingHeader>
      {!!postsObj.mainPost && (
        <Grid gutter={0} gutterMd={rem(24)}>
          {/* Main post */}
          {postsObj.mainPost && (
            <Grid.Col
              span={12}
              md={6}
              sx={(theme) => ({
                '.cardArticleTitle': {
                  fontSize: rem(22),
                  lineHeight: rem(32),
                  letterSpacing: '-0.6px',
                  [theme.fn.smallerThan('sm')]: {
                    fontSize: rem(20),
                    lineHeight: rem(28),
                    letterSpacing: '-0.4px',
                  },
                },
              })}
            >
              <TrendingArticleCard
                article={postsObj.mainPost}
                index={1}
                activeTab={activeTab}
                currentDomain={currentDomain}
                loading={loading}
                loadingBookmark={loadingBookmark}
                bookmarks={bookmarks}
                onClickBookmark={onClickBookmark}
                className="homeTrendingMain"
              />
            </Grid.Col>
          )}
          {!!postsObj.asidePosts.length && (
            <Grid.Col span={12} md={6}>
              <StyledHomeTrendingList>
                <Grid gutter={0} gutterMd={rem(24)}>
                  {postsObj.asidePosts.map((post, idx) => (
                    <Grid.Col
                      key={`${idx}-${post.ID}`}
                      span={12}
                      md={postsObj.trendingPosts.length == 6 ? 6 : 12}
                    >
                      <TrendingArticleCard
                        article={post}
                        index={idx + 2}
                        activeTab={activeTab}
                        currentDomain={currentDomain}
                        loading={loading}
                        loadingBookmark={loadingBookmark}
                        bookmarks={bookmarks}
                        onClickBookmark={onClickBookmark}
                        showExcerpt={
                          !isMobile && postsObj.trendingPosts.length == 6
                        }
                        type={
                          !isMobile && postsObj.trendingPosts.length == 6
                            ? 'vertical'
                            : 'horizontal'
                        }
                        className="homeTrendingSub"
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              </StyledHomeTrendingList>
            </Grid.Col>
          )}
        </Grid>
      )}
      {/* Bottom posts */}
      {!!postsObj.bottomPosts.length && (
        <SimpleGrid
          cols={1}
          spacing={0}
          breakpoints={[
            {
              minWidth: Breakpoints.BREAK_POINT_TABLET,
              cols: postsObj.bottomPosts.length,
              spacing: 24,
            },
          ]}
        >
          {postsObj.bottomPosts.map((post, idx) => (
            <Box
              key={`${idx}-${post.ID}`}
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  borderTop: `1px solid ${theme.colors.neutral100}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              })}
            >
              <TrendingArticleCard
                article={post}
                index={idx + 5}
                activeTab={activeTab}
                currentDomain={currentDomain}
                loading={loading}
                loadingBookmark={loadingBookmark}
                bookmarks={bookmarks}
                onClickBookmark={onClickBookmark}
                type={isMobile ? 'horizontal' : undefined}
              />
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  )
}
